import React, { useState, useEffect } from 'react';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { MobileBottomBar } from './components/layout/MobileBottomBar';
import { ConsultationModal } from './components/ui/ConsultationModal';
import { StartProjectModal } from './components/ui/StartProjectModal';
import { ProjectModal } from './components/ui/ProjectModal';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { ServicesPage } from './pages/ServicesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { MarketPage } from './pages/MarketPage';
import { IndustriesPage } from './pages/IndustriesPage';
import { PortfolioPage } from './pages/PortfolioPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { CareersPage } from './pages/CareersPage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsConditionsPage } from './pages/TermsConditionsPage';
import { ProjectItem } from './types';

export const App: React.FC = () => {
  const [currentPath, setCurrentPath] = useState<string>(() => {
    return window.location.pathname || '/';
  });

  const [consultationOpen, setConsultationOpen] = useState(false);
  const [consultationService, setConsultationService] = useState<string>('');
  const [startProjectOpen, setStartProjectOpen] = useState(false);
  const [startProjectService, setStartProjectService] = useState<string>('Website Development');
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);

  // Sync with browser history back/forward
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    if (path !== currentPath) {
      window.history.pushState({}, '', path);
      setCurrentPath(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleOpenConsultation = (serviceName: string = '') => {
    setConsultationService(serviceName);
    setConsultationOpen(true);
  };

  const handleOpenStartProject = (serviceName: string = 'Website Development') => {
    setStartProjectService(serviceName);
    setStartProjectOpen(true);
  };

  const renderCurrentPage = () => {
    // Route: /
    if (currentPath === '/' || currentPath === '') {
      return (
        <HomePage
          onNavigate={navigate}
          onOpenConsultation={(service) => handleOpenConsultation(service)}
          onStartProject={() => handleOpenStartProject()}
          onSelectProject={(project) => setSelectedProject(project)}
        />
      );
    }

    // Route: /about
    if (currentPath === '/about') {
      return (
        <AboutPage
          onNavigate={navigate}
          onOpenConsultation={() => handleOpenConsultation()}
        />
      );
    }

    // Route: /services
    if (currentPath === '/services') {
      return (
        <ServicesPage
          onSelectService={(slug) => navigate(`/services/${slug}`)}
          onOpenConsultation={(service) => handleOpenConsultation(service)}
        />
      );
    }

    // Route: /services/:slug
    if (currentPath.startsWith('/services/')) {
      const slug = currentPath.replace('/services/', '');
      return (
        <ServiceDetailPage
          slug={slug}
          onNavigate={navigate}
          onOpenConsultation={(service) => handleOpenConsultation(service || slug)}
        />
      );
    }

    // Route: /markets and /markets/:country
    if (currentPath === '/markets' || currentPath.startsWith('/markets/')) {
      let initialMarket: 'usa' | 'uk' | 'canada' = 'usa';
      if (currentPath.includes('uk')) initialMarket = 'uk';
      if (currentPath.includes('canada')) initialMarket = 'canada';
      return (
        <MarketPage
          initialMarket={initialMarket}
          onNavigate={navigate}
          onStartProject={() => handleOpenStartProject()}
          onOpenConsultation={() => handleOpenConsultation()}
        />
      );
    }

    // Route: /industries
    if (currentPath === '/industries') {
      return (
        <IndustriesPage
          onNavigate={navigate}
          onStartProject={() => handleOpenStartProject()}
          onOpenConsultation={() => handleOpenConsultation()}
        />
      );
    }

    // Route: /start-project
    if (currentPath === '/start-project') {
      return (
        <HomePage
          onNavigate={navigate}
          onOpenConsultation={(service) => handleOpenConsultation(service)}
          onStartProject={() => handleOpenStartProject()}
          onSelectProject={(project) => setSelectedProject(project)}
        />
      );
    }

    // Route: /portfolio
    if (currentPath === '/portfolio') {
      return (
        <PortfolioPage
          onSelectProject={(project) => setSelectedProject(project)}
          onOpenConsultation={() => handleOpenConsultation()}
        />
      );
    }

    // Route: /blog
    if (currentPath === '/blog') {
      return (
        <BlogPage
          onSelectPost={(slug) => navigate(`/blog/${slug}`)}
        />
      );
    }

    // Route: /blog/:slug
    if (currentPath.startsWith('/blog/')) {
      const slug = currentPath.replace('/blog/', '');
      return (
        <BlogPostPage
          slug={slug}
          onNavigate={navigate}
          onOpenConsultation={() => handleOpenConsultation()}
        />
      );
    }

    // Route: /careers
    if (currentPath === '/careers') {
      return (
        <CareersPage
          onNavigate={navigate}
          onOpenConsultation={() => handleOpenConsultation()}
        />
      );
    }

    // Route: /contact
    if (currentPath === '/contact') {
      return <ContactPage />;
    }

    // Route: /privacy
    if (currentPath === '/privacy') {
      return <PrivacyPolicyPage onNavigate={navigate} />;
    }

    // Route: /terms
    if (currentPath === '/terms') {
      return <TermsConditionsPage onNavigate={navigate} />;
    }

    // Default Fallback
    return (
      <HomePage
        onNavigate={navigate}
        onOpenConsultation={(service) => handleOpenConsultation(service)}
        onStartProject={() => handleOpenStartProject()}
        onSelectProject={(project) => setSelectedProject(project)}
      />
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#071426] text-[#D5DFEC] selection:bg-[#FF851B] selection:text-white">
      {/* Sticky Header */}
      <Header
        currentPath={currentPath}
        onNavigate={navigate}
        onOpenConsultation={() => handleOpenConsultation()}
        onStartProject={() => handleOpenStartProject()}
      />

      {/* Main Page Content */}
      <main className="flex-grow">
        {renderCurrentPage()}
      </main>

      {/* Footer */}
      <Footer onNavigate={navigate} />

      {/* Mobile Sticky Quick-Action Bar for iOS & Android */}
      <MobileBottomBar
        onOpenConsultation={() => handleOpenConsultation()}
        onStartProject={() => handleOpenStartProject()}
      />

      {/* 5-Step Guided Project Scoping Modal ("Start Your Project") */}
      <StartProjectModal
        isOpen={startProjectOpen}
        onClose={() => setStartProjectOpen(false)}
        defaultService={startProjectService}
      />

      {/* Consultation Modal */}
      <ConsultationModal
        isOpen={consultationOpen}
        onClose={() => setConsultationOpen(false)}
        defaultService={consultationService}
      />

      {/* Project Details Modal */}
      <ProjectModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onConsult={() => handleOpenConsultation('Custom Software & App Engineering')}
      />
    </div>
  );
};
